import React, { useMemo } from 'react';
import type { FC } from 'react';
import snakeCase from 'lodash/snakeCase.js';
import { TextInput, useAsyncCallback } from '@wix/cli-ui-kit';
import { useTranslation } from '@wix/cli-i18n';
import {
  useDevCenterClient,
  SetCodeIdentifierResult,
} from '@wix/dev-center-client';
import { isType } from 'variant';
import { useCreateAppBi } from '../bi/index.js';
import { validateCodeIdentifier } from '../validations/index.js';

interface ChooseCodeIdentifierProps {
  onSubmit: (codeIdentifier: string) => void;
  appId: string;
  packageName: string;
}

export const ChooseCodeIdentifier: FC<ChooseCodeIdentifierProps> = ({
  onSubmit,
  appId,
  packageName,
}) => {
  const devCenterClient = useDevCenterClient();
  const { t } = useTranslation();
  const { cliFlowStepAnswered } = useCreateAppBi();
  const defaultCodeIdentifier = useMemo(
    () => snakeCase(packageName),
    [packageName]
  );
  const validationFn = useAsyncCallback(
    async (_, submitedCodeIdentifier: string) => {
      const isValid = validateCodeIdentifier(submitedCodeIdentifier);
      if (isValid !== true) {
        return isValid;
      }

      const result = await devCenterClient.setCodeIdentifier({
        appId,
        codeIdentifier: submitedCodeIdentifier,
      });

      if (isType(result, SetCodeIdentifierResult.ShouldBeUnique)) {
        return `Code identifier ${submitedCodeIdentifier} is already used. Try another.`;
      }

      onSubmit(result.codeIdentifier);

      return true;
    }
  );

  return (
    <TextInput
      label={t('create_app.choose_code_identifier.label')}
      placeholder={t('create_app.choose_code_identifier.placeholder', {
        suggested: defaultCodeIdentifier,
      })}
      validate={(submitedCodeIdentifier) =>
        validationFn.execute(submitedCodeIdentifier)
      }
      onSubmit={(codeIdentifier) => {
        cliFlowStepAnswered({
          question: t('create_app.choose_code_identifier.label'),
          questionKey: 'create_app.choose_code_identifier.label',
          answer: codeIdentifier,
        });
      }}
      inCreateFlow
    />
  );
};
